import { Injectable } from '@angular/core';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

@Injectable({
  providedIn: 'root',
})
export class SortService {
  /**
   * Sets a default sort algorithm that ignores case [A-z] since Material does not provide this.
   * @param source is the data that you want to sort.
   * @param sort is the sort object to set.
   * @returns the source since TypeScript is pass by reference and the original source must be replaced.
   */
  public setDefaultSort<T>(source: MatTableDataSource<T>, sort: MatSort): MatTableDataSource<T> {
    const sorter = (item: T, property: string) => {
      // If the item is a string, ignore its case, otherwise Material will handle it accordingly.
      return typeof item[property] === 'string' ? item[property].toLowerCase() : item[property];
    };
    source.sortingDataAccessor = sorter;
    source.sort = sort;
    return source;
  }

  /**
   * Sets a sort algorithm for a column which needs to sort first on one value, then on another.
   * Calls hellper function, _sort().
   * Ex. [{1,2},{2,2},{1,1}] => [{1,1},{1,2},{2,2}]
   * @param source is the data that you want to sort.
   * @param sort is the sort object to set.
   * @param primaryKey is the key to sort on first.
   * @param secondaryKey is the key to sort on second.
   * @returns the source since TypeScript is pass by reference and the original source must be replaced.
   */
  public setSecondarySort<T>(
    source: MatTableDataSource<T>,
    sort: MatSort,
    primaryKey: string,
    secondaryKey: string
  ): MatTableDataSource<T> {
    source.data = this._sort(source.data, sort, primaryKey, secondaryKey);
    source.sort = sort;
    return source;
  }

  /**
   * Compares two items first by one value, then another.
   * TODO: handle string case; currently works for numbers and booleans.
   * @param a is the first element.
   * @param b is the second element.
   * @param sortDirection is ascending or descending.
   * @param primaryKey is the first key to sort on.
   * @param secondaryKey is the second key to sort on.
   * @returns a positive number, a negative number, or 0 for a sorting algorithm to compare the two elements.
   */
  private _compare<T>(a: T, b: T, sortDirection: SortDirection, primaryKey: string, secondaryKey: string): number {
    // If values are booleans, cast to 0/1 using Number().
    const primaryA = typeof a[primaryKey] === 'boolean' ? Number(a[primaryKey]) : a[primaryKey];
    const primaryB = typeof b[primaryKey] === 'boolean' ? Number(b[primaryKey]) : b[primaryKey];
    const secondaryA = typeof a[secondaryKey] === 'boolean' ? Number(a[secondaryKey]) : a[secondaryKey];
    const secondaryB = typeof b[secondaryKey] === 'boolean' ? Number(b[secondaryKey]) : b[secondaryKey];

    // Compare the two primary values.
    const primaryComparison = primaryA - primaryB;

    // If the two primary values are different, we just return the comparison.
    if (primaryComparison !== 0) {
      // Reverse comparison for descending.
      return primaryComparison * (sortDirection === 'asc' ? 1 : -1);
    }

    // If the two primary values are the same, we compare the two secondary values.
    // Reverse comparison for descending.
    return (secondaryA - secondaryB) * (sortDirection === 'asc' ? 1 : -1);
  }

  /**
   * Sets up sorting using a comparison of first a primary value, then a secondary.
   * Calls helper function _compare().
   * @param data is the data to sort.
   * @param sort is used for the direction of the sort; ascending or descending.
   * @param primaryKey is the key of the values to sort on first.
   * @param secondaryKey is the key of the values to sort on second.
   * @returns the sorted data.
   */
  private _sort<T>(data: T[], sort: Sort, primaryKey: string, secondaryKey: string): T[] {
    const sortedData: T[] = data.slice();
    if (!sort.active || sort.direction === '') {
      return sortedData;
    }

    return sortedData.sort((a, b) => {
      return this._compare(a, b, sort.direction, primaryKey, secondaryKey);
    });
  }
}
